import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { UsuarioService } from '../../services/usuario.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrderEmergenciaService } from 'src/app/services/order-emergencia.service';

@Component({
    selector: 'app-order-emergencias',
    templateUrl: './order-emergencias.component.html',
    styleUrls: ['./order-emergencias.component.css'],
    standalone: false
})
export class OrderEmergenciasComponent implements OnInit, OnDestroy {
  private _usuarioService = inject(UsuarioService);
  private _orderEmergenciaService = inject(OrderEmergenciaService);
  private router = inject(Router);
  private location = inject(Location);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);

  // ===== estado (signals) =====
  error = signal<string>('');

  // === estado (signals) ===
  asociadoCardio = computed(() => this._usuarioService.asociadoCardio());
  helpOpen = signal<boolean>(false);
  arming = signal(false);
  
  cancelEmergency = signal<boolean>(false);
  count = signal<number>(5);
  alertaEnviada = signal<boolean>(false);
  envioFallido = signal<boolean>(false);
  
  idPerfil = computed<number>(() => this._usuarioService.usuario().id_perfil ?? 2);
  xTelefonoEmergencia = computed<string>(() => this._usuarioService.usuario().x_telefono_emergencia ?? '');
  
  private emergencyTimer: ReturnType<typeof setInterval> | null = null;

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // Si necesitás (re)cargar usuario/cliente desde backend, hacelo bajo el loader:
    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        await this._authService.ensureSession();
        await this._usuarioService.refreshAsociadoCardio();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
      }
    });
  }

  ngOnDestroy(): void {
    this.clearEmergencyTimer();
  }

  // === UI ===
  back(): void {
    if (history.length > 1) this.location.back();
    else this.router.navigateByUrl('/dashboard');
  }

  openhelp(): void {
    this.helpOpen.update(v => !v);
  }

  helpText = computed(() => {
    switch (this.idPerfil()) {
      case 1: return 'Si sos socio de Cardio Emergencias Médicas, podrás encontrar el boton de emergencia y los principales accesos directos a los botones policía, bomberos, familiar y Cardio.';
      case 2: return 'Si sos socio de Cardio Emergencias Médicas, podrás encontrar el boton de emergencia y los principales accesos directos a los botones policía, bomberos, familiar y Cardio.';
      case 3: return 'Si sos socio de Cardio Emergencias Médicas, podrás encontrar el boton de emergencia y los principales accesos directos a los botones policía, bomberos, familiar y Cardio.';
      case 4: return 'Si sos socio de Cardio Emergencias Médicas, podrás encontrar el boton de emergencia y los principales accesos directos a los botones policía, bomberos, familiar y Cardio.';
      default: return 'Si sos socio de Cardio Emergencias Médicas, podrás encontrar el boton de emergencia y los principales accesos directos a los botones policía, bomberos, familiar y Cardio.';
    }
  });

  solicitarEmergencia(): void {
    // Evitar múltiples timers
    if (this.emergencyTimer) return;

    this.cancelEmergency.set(true);
    this.count.set(5);

    this.emergencyTimer = setInterval(() => {
      if (!this.cancelEmergency()) {
        this.clearEmergencyTimer();
        this.count.set(5);
        return;
      }

      this.count.update(c => c - 1);

      if (this.count() <= 0) {
        this.clearEmergencyTimer();
        if (this.cancelEmergency()) {
          this.enviarWhatsappEmergency();
        }
      }
    }, 1000);

    console.log('Enviando solicitud de emergencia en 5s (cancelable)...');
  }

  private enviarWhatsappEmergency(): void {
    const clienteId = this._usuarioService.cliente().id;
    if (!clienteId) {
      // No tenemos los datos del cliente cargados (p.ej. falló la carga al
      // abrir la app). Antes esto no hacía nada: el usuario veía terminar la
      // cuenta regresiva y creía que ya se había avisado, cuando en realidad
      // nunca se envió nada. Mostramos el fallback con el teléfono directo.
      console.error('enviarWhatsappEmergency: falta id de cliente, no se pudo enviar la alerta');
      this.cancelEmergency.set(false);
      this.envioFallido.set(true);
      return;
    }

    this._orderEmergenciaService.sendWhatsappEmergency(clienteId, false).subscribe({
      next: () => {
        this.cancelEmergency.set(false);
        this.alertaEnviada.set(true);
      },
      error: (e) => {
        console.error(e?.error?.msg ?? e);
        this.cancelEmergency.set(false);
        this.envioFallido.set(true);
      },
      complete: () => console.log('sendWhatsappEmergency finalizado'),
    });
  }

  // Reintenta la alerta: primero intenta recargar los datos del usuario (por
  // si lo que faltaba era justamente eso), y recién ahí rearma la alerta —
  // reintentar sin recargar los datos volvería a fallar en silencio con el
  // mismo id de cliente vacío.
  async reintentarEmergencia(): Promise<void> {
    this.envioFallido.set(false);
    if (!this._usuarioService.cliente().id) {
      await this._loadingService.run(async () => {
        try {
          await this._authService.ensureSession();
        } catch (e) {
          console.error(e);
        }
      });
    }
    this.solicitarEmergencia();
  }

  cancelSolicitarEmergencia(): void {
    this.cancelEmergency.set(false);
    this.clearEmergencyTimer();
    this.count.set(5);
  }

  private clearEmergencyTimer(): void {
    if (this.emergencyTimer) {
      clearInterval(this.emergencyTimer);
      this.emergencyTimer = null;
    }
  }

  armarEmergencia(): void {
    if (this.arming() || this.cancelEmergency() || this.emergencyTimer) return;

    this.arming.set(true);

    // Esperamos 500ms (animación) y luego ejecutamos la lógica original
    setTimeout(() => {
      this.arming.set(false);
      this.solicitarEmergencia();   // usa tu método existente
    }, 500);
  }
}